<template>
  <div class="mt-2">
    <b-breadcrumb class="breadcrumb-pipes">
      <b-breadcrumb-item href="/settings/admin-panel/">
        Admin Panel
      </b-breadcrumb-item>
      <b-breadcrumb-item href="/settings/user-role">
        Roles and Permissions
      </b-breadcrumb-item>
      <b-breadcrumb-item href="/settings/personal-settings">
        Personal Settings
      </b-breadcrumb-item>
    </b-breadcrumb>

    <b-card class="mb-0" title="Venue Settings">
      <b-overlay :show="loading" rounded="sm" no-fade>
        <div class="m-2">
          <b-row align-h="between">
            <b-col cols="12" md="3">
              <b-form-input
                v-model="filter"
                class="d-inline-block mr-1"
                placeholder="Search..."
              />
            </b-col>
            <b-col cols="12" md="3">
              <div class="d-flex align-items-center justify-content-end">
                <b-button variant="success" @click="openCreateModal">
                  <span class="text-nowrap">Add Venue</span>
                </b-button>
              </div>
            </b-col>
          </b-row>
        </div>

        <b-table
          striped
          hover
          ref="refVenueTable"
          class="position-relative p-2"
          :items="allVenues"
          :filter="filter"
          filter-debounce="250"
          @filtered="onFiltered"
          :filter-included-fields="filterOn"
          selectable
          select-mode="single"
          responsive
          :small="true"
          head-variant="dark"
          :fields="tableColumns"
          show-empty
          empty-text="No matching records found"
          :current-page="currentPage"
          :per-page="perPage"
        >
          <template #cell(index)="data">
            <b><span class="font-weight-bold">
              <strong class="text-primary">#{{ data.index + 1 }}</strong>
            </span></b>
          </template>

          <template #cell(Name)="data">
            <strong>{{ data.item.Name || 'N/A' }}</strong>
          </template>

          <template #cell(Swift)="data">
            <span>{{ data.item.Swift || 'N/A' }}</span>
          </template>

          <template #cell(Abbreviation)="data">
            <span>{{ data.item.Abbreviation || 'N/A' }}</span>
          </template>

          <template #cell(actions)="data">
            <b-dropdown
              variant="link"
              no-caret
              :right="$store.state.appConfig.isRTL"
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>
              <b-dropdown-item @click="openEditModal(data.item)">
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">Edit</span>
              </b-dropdown-item>
              <b-dropdown-item @click="confirmDelete(data.item)">
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50">Delete</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>

        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted">
                Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries
              </span>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-overlay>

      <!-- Create Modal -->
      <b-modal
        v-model="createModal"
        title="Add Venue"
        @hidden="resetCreateForm"
        size="md"
        :ok-title="'Create'"
        cancel-title="Cancel"
        @ok="handleCreate"
      >
        <b-overlay :show="modalLoading" rounded="sm" no-fade>
          <b-form>
            <validation-observer ref="createFormValidation">
              <b-form-group label="Name" label-for="venue-name">
                <validation-provider #default="{ errors }" name="Name" rules="required">
                  <b-form-input
                    id="venue-name"
                    v-model="createForm.Name"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Venue name"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label="Swift" label-for="venue-swift">
                <validation-provider #default="{ errors }" name="Swift">
                  <b-form-input
                    id="venue-swift"
                    v-model="createForm.Swift"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Swift code"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label="Abbreviation" label-for="venue-abbreviation">
                <validation-provider #default="{ errors }" name="Abbreviation">
                  <b-form-input
                    id="venue-abbreviation"
                    v-model="createForm.Abbreviation"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Abbreviation"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </validation-observer>
          </b-form>
        </b-overlay>
      </b-modal>

      <!-- Edit Modal -->
      <b-modal
        v-model="editModal"
        title="Edit Venue"
        @hidden="resetEditForm"
        size="md"
        :ok-title="'Update'"
        cancel-title="Cancel"
        @ok="handleUpdate"
      >
        <b-overlay :show="modalLoading" rounded="sm" no-fade>
          <b-form>
            <validation-observer ref="editFormValidation">
              <b-form-group label="Name" label-for="edit-venue-name">
                <validation-provider #default="{ errors }" name="Name" rules="required">
                  <b-form-input
                    id="edit-venue-name"
                    v-model="editForm.Name"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Venue name"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label="Swift" label-for="edit-venue-swift">
                <validation-provider #default="{ errors }" name="Swift">
                  <b-form-input
                    id="edit-venue-swift"
                    v-model="editForm.Swift"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Swift code"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label="Abbreviation" label-for="edit-venue-abbreviation">
                <validation-provider #default="{ errors }" name="Abbreviation">
                  <b-form-input
                    id="edit-venue-abbreviation"
                    v-model="editForm.Abbreviation"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Abbreviation"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </validation-observer>
          </b-form>
        </b-overlay>
      </b-modal>
    </b-card>
  </div>
</template>

<script>
import {
  BOverlay,
  BCard,
  BRow,
  BCol,
  BFormInput,
  BFormGroup,
  BButton,
  BTable,
  BDropdown,
  BDropdownItem,
  BPagination,
  BModal,
  BForm,
  BBreadcrumb,
  BBreadcrumbItem,
  VBModal,
} from 'bootstrap-vue';
import store from '@/store';
import { onUnmounted } from '@vue/composition-api';
import settingsStoreModule from '../../settingsStoreModule';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { required } from '@validations';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import Ripple from 'vue-ripple-directive';

export default {
  components: {
    BOverlay,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BFormGroup,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BModal,
    BForm,
    BBreadcrumb,
    BBreadcrumbItem,
    ValidationProvider,
    ValidationObserver,
    ToastificationContent,
  },

  directives: {
    Ripple,
    'b-modal': VBModal,
  },

  setup() {
    const SETTINGS_APP_STORE_MODULE_NAME = 'settings';
    if (!store.hasModule(SETTINGS_APP_STORE_MODULE_NAME))
      store.registerModule(SETTINGS_APP_STORE_MODULE_NAME, settingsStoreModule);

    onUnmounted(() => {
      if (store.hasModule(SETTINGS_APP_STORE_MODULE_NAME))
        store.unregisterModule(SETTINGS_APP_STORE_MODULE_NAME);
    });
  },

  data() {
    return {
      allVenues: [],
      loading: false,
      modalLoading: false,
      createModal: false,
      editModal: false,
      createForm: { Name: null, Swift: null, Abbreviation: null },
      editForm: { id: null, Name: null, Swift: null, Abbreviation: null },
      filter: null,
      filterOn: ['Name', 'Swift', 'Abbreviation'],
      tableColumns: [
        { key: 'index', label: '' },
        { key: 'Name', label: 'Name', sortable: true },
        { key: 'Swift', label: 'Swift', sortable: true },
        { key: 'Abbreviation', label: 'Abbreviation', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
      perPage: 10,
      currentPage: 1,
      totalRows: 0,
    };
  },

  computed: {
    dataMeta() {
      return {
        from: this.totalRows !== 0 ? this.perPage * (this.currentPage - 1) + 1 : 0,
        to:
          this.perPage * (this.currentPage - 1) + Number(this.perPage) < this.totalRows
            ? this.perPage * (this.currentPage - 1) + Number(this.perPage)
            : this.totalRows,
        of: this.totalRows,
      };
    },
  },

  mounted() {
    this.getVenues();
  },

  methods: {
    getVenues() {
      this.loading = true;
      store
        .dispatch('settings/getVenues')
        .then((res) => {
          this.allVenues = res.data;
          this.totalRows = res.data.length;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.showErrorToast('Error fetching venues.');
        });
    },

    openCreateModal() {
      this.createModal = true;
    },

    openEditModal(item) {
      this.editForm = { id: item.id, Name: item.Name, Swift: item.Swift, Abbreviation: item.Abbreviation };
      this.editModal = true;
    },

    handleCreate(bvModalEvent) {
      bvModalEvent.preventDefault();
      this.$refs.createFormValidation.validate().then((success) => {
        if (success) {
          this.modalLoading = true;
          store
            .dispatch('settings/createVenue', this.createForm)
            .then((res) => {
              this.modalLoading = false;
              if (res.data.info === 'exist_name') {
                this.showWarningToast('A venue with this name already exists.');
              } else if (res.data.status) {
                this.createModal = false;
                this.showSuccessToast('Venue created successfully.');
                this.getVenues();
              } else {
                this.showErrorToast('Something went wrong.');
              }
            })
            .catch(() => {
              this.modalLoading = false;
              this.showErrorToast('Something went wrong.');
            });
        }
      });
    },

    handleUpdate(bvModalEvent) {
      bvModalEvent.preventDefault();
      this.$refs.editFormValidation.validate().then((success) => {
        if (success) {
          this.modalLoading = true;
          store
            .dispatch('settings/updateVenue', this.editForm)
            .then((res) => {
              this.modalLoading = false;
              if (res.data.info === 'exist_name') {
                this.showWarningToast('A venue with this name already exists.');
              } else if (res.data.status) {
                this.editModal = false;
                this.showSuccessToast('Venue updated successfully.');
                this.getVenues();
              } else {
                this.showErrorToast('Something went wrong.');
              }
            })
            .catch(() => {
              this.modalLoading = false;
              this.showErrorToast('Something went wrong.');
            });
        }
      });
    },

    confirmDelete(item) {
      this.$bvModal
        .msgBoxConfirm(`Are you sure you want to delete "${item.Name}"?`, {
          title: 'Confirm Delete',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          centered: true,
        })
        .then((confirmed) => {
          if (confirmed) {
            this.deleteVenue(item);
          }
        });
    },

    deleteVenue(item) {
      this.loading = true;
      store
        .dispatch('settings/deleteVenue', { id: item.id })
        .then((res) => {
          if (res.data.status) {
            this.showSuccessToast('Venue deleted successfully.');
            this.getVenues();
          } else {
            this.loading = false;
            this.showErrorToast('Something went wrong.');
          }
        })
        .catch(() => {
          this.loading = false;
          this.showErrorToast('Something went wrong.');
        });
    },

    resetCreateForm() {
      this.createForm = { Name: null, Swift: null, Abbreviation: null };
      this.modalLoading = false;
      if (this.$refs.createFormValidation) this.$refs.createFormValidation.reset();
    },

    resetEditForm() {
      this.editForm = { id: null, Name: null, Swift: null, Abbreviation: null };
      this.modalLoading = false;
      if (this.$refs.editFormValidation) this.$refs.editFormValidation.reset();
    },

    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    showSuccessToast(text) {
      this.$toast({
        component: ToastificationContent,
        props: { title: 'Success', text, icon: 'ThumbsUpIcon', variant: 'success' },
      });
    },

    showWarningToast(text) {
      this.$toast({
        component: ToastificationContent,
        props: { title: 'Warning', text, icon: 'AlertTriangleIcon', variant: 'warning' },
      });
    },

    showErrorToast(text) {
      this.$toast({
        component: ToastificationContent,
        props: { title: 'Error', text, icon: 'AlertTriangleIcon', variant: 'danger' },
      });
    },
  },

  watch: {
    perPage: {
      handler(val) {
        localStorage.setItem('venueShow', val);
      },
    },
  },
};
</script>
